import { Link } from 'react-router-dom';
import { useSubscription } from '@/context/SubscriptionContext';
import { Sparkles } from 'lucide-react';

/**
 * Trial countdown pill — visible only for users with an active 7-day trial.
 *
 * Color schedule:
 *   Days 1-4 → green
 *   Days 5-6 → amber
 *   Day 7    → red
 *
 * Click → /pricing (where they can upgrade and keep their data).
 */
export default function TrialCountdownPill() {
  const { tier, trialDaysRemaining } = useSubscription();

  // Only show for active trial users
  if (tier !== 'trial') return null;

  // Backend returns the *remaining* days. Convert to "Day X of 7".
  // trialDaysRemaining of 6 ⇒ Day 1, 5 ⇒ Day 2, …, 0 ⇒ Day 7.
  const remaining =
    typeof trialDaysRemaining === 'number' ? Math.max(0, trialDaysRemaining) : null;
  if (remaining === null) return null;
  const dayNumber = Math.min(7, Math.max(1, 7 - remaining));

  const palette =
    dayNumber >= 7
      ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-300 dark:hover:bg-red-900/60 border-red-200 dark:border-red-800'
      : dayNumber >= 5
      ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:hover:bg-amber-900/60 border-amber-200 dark:border-amber-800'
      : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:hover:bg-emerald-900/60 border-emerald-200 dark:border-emerald-800';

  return (
    <Link
      to="/pricing"
      data-testid="trial-countdown-pill"
      title={`Pro Trial — Day ${dayNumber} of 7. Click to upgrade and keep all your matches, notes, and saved cases.`}
      className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${palette}`}
    >
      <Sparkles className="w-3.5 h-3.5" />
      <span>Pro Trial: Day {dayNumber} of 7</span>
    </Link>
  );
}
